import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm no-underline">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          image: article.imageUrl,
          datePublished: article.publishDate,
          inLanguage: 'zh-CN',
          articleSection: category?.name || '',
          url: `https://magnetlink-guide.com/article/${slug}`,
          author: {
            '@type': 'Organization',
            name: '磁力链指南',
          },
          publisher: {
            '@type': 'Organization',
            name: '磁力链指南',
          },
        })}
      </script>

      {/* Article Header */}
      <section className="pt-24 pb-8 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="mb-4">
            <Breadcrumb items={getArticleBreadcrumb(slug || '')} />
          </div>

          <div className="flex items-center gap-3 mb-4">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors duration-200 no-underline whitespace-nowrap"
              >
                {category.name}
              </Link>
            )}
          </div>

          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>

          <p className="text-base text-foreground-600 mb-6 leading-relaxed">
            {article.summary}
          </p>

          <div className="flex items-center gap-4 text-sm text-foreground-400">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line"></i>
              </span>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              阅读约 {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-background-100 pb-8">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="aspect-[16/9] rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-10 md:py-14 bg-background-50">
        <article className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
              prose-li:text-foreground-700 prose-li:text-base prose-li:leading-relaxed
              prose-strong:text-foreground-900 prose-strong:font-semibold
              prose-table:border prose-table:border-background-200
              prose-th:bg-background-100 prose-th:px-4 prose-th:py-2 prose-th:text-sm prose-th:font-semibold prose-th:text-foreground-800
              prose-td:px-4 prose-td:py-2 prose-td:text-sm prose-td:text-foreground-700 prose-td:border prose-td:border-background-200
              prose-pre:bg-background-100 prose-pre:text-foreground-800 prose-pre:rounded-lg prose-pre:text-sm
              prose-code:text-primary-600 prose-code:bg-primary-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-ol:pl-6 prose-ul:pl-6
              [&_ol]:list-decimal [&_ul]:list-disc
              [&_ol>li]:pl-1.5 [&_ul>li]:pl-1.5
              [&_ol>li::marker]:text-primary-500 [&_ul>li::marker]:text-accent-500
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 no-underline"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={rel.imageUrl}
                      alt={rel.title}
                      title={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{rel.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to top / nav */}
      <section className="py-8 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 no-underline"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              返回 {category.name}
            </Link>
          )}
        </div>
      </section>
    </>
  );
}